import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
} from '@mui/material';
import {
  AccountBalance as AccountIcon,
  MonetizationOn as MonetizationIcon,
  TrendingUp as TrendingUpIcon,
  PendingActions as PendingIcon,
  CheckCircle as PaidIcon,
  AttachMoney as ProfitIcon,
  Savings as SavingsIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ResponsiveAppBar from '../components/AppBar';
import { API_BASE_URL } from '../api/config';
import CustomerSearchBar, { matchCustomerSearch } from '../components/CustomerSearchBar';

// Create theme with specified color scheme
const theme = createTheme({
  palette: {
    primary: { main: '#77c8c7' }, // Teal
    secondary: { main: '#936097' }, // Plum
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    error: { main: '#ef4444' },
    success: { main: '#22c55e' },
  },
  typography: {
    fontFamily: '"Outfit", "Poppins", "Roboto", sans-serif',
    h4: {
      fontWeight: 800,
      letterSpacing: '-0.5px',
      color: '#1e293b',
    },
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.3px',
      color: '#1e293b',
    },
    subtitle1: {
      fontWeight: 500,
      color: '#64748b',
      fontSize: '1.05rem',
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 22px',
          boxShadow: '0 4px 12px rgba(147, 96, 151, 0.08)',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(119, 200, 199, 0.25)',
          },
        },
      },
    },
  },
});

const InterestCalculation = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Filter entries based on customer search query (Refer ID, Name, Contact, Aadhar)
  const filteredEntries = entries.filter((entry) => matchCustomerSearch(entry, searchQuery));

  // Calculate totals for the specified fields dynamically based on filtered entries
  const totals = filteredEntries.reduce(
    (acc, entry) => ({
      lendedAmount: acc.lendedAmount + (Number(entry.lendedAmount) || 0),
      remainingDues: acc.remainingDues + (Number(entry.remainingDues) || 0),
      pendingDue: acc.pendingDue + (Number(entry.pendingDue) || 0),
      totalPrincipalDue: acc.totalPrincipalDue + (Number(entry.totalPrincipalDue) || 0),
      totalInterestDue: acc.totalInterestDue + (Number(entry.totalInterestDue) || 0),
      paidDueAmount: acc.paidDueAmount + (Number(entry.paidDueAmount) || 0),
      remainingPrincipalTobePaid: acc.remainingPrincipalTobePaid + (Number(entry.remainingPrincipalTobePaid) || 0),
      profit: acc.profit + (Number(entry.profit) || 0),
    }),
    {
      lendedAmount: 0,
      remainingDues: 0,
      pendingDue: 0,
      totalPrincipalDue: 0,
      totalInterestDue: 0,
      paidDueAmount: 0,
      remainingPrincipalTobePaid: 0,
      profit: 0,
    }
  );

  const totalCards = [
    { label: 'Total Lended Capital', value: totals.lendedAmount, isCurrency: true, icon: <MonetizationIcon />, color: '#0f766e', bg: 'rgba(119, 200, 199, 0.15)' },
    { label: 'Remaining Dues Count', value: totals.remainingDues, isCurrency: false, icon: <PendingIcon />, color: '#936097', bg: 'rgba(147, 96, 151, 0.12)' },
    { label: 'Pending Dues Count', value: totals.pendingDue, isCurrency: false, icon: <ReceiptIcon />, color: '#b45309', bg: 'rgba(245, 158, 11, 0.12)' },
    { label: 'Principal in Dues', value: totals.totalPrincipalDue, isCurrency: true, icon: <SavingsIcon />, color: '#0f766e', bg: 'rgba(119, 200, 199, 0.15)' },
    { label: 'Interest in Dues', value: totals.totalInterestDue, isCurrency: true, icon: <TrendingUpIcon />, color: '#936097', bg: 'rgba(147, 96, 151, 0.12)' },
    { label: 'Due Amount Paid', value: totals.paidDueAmount, isCurrency: true, icon: <PaidIcon />, color: '#15803d', bg: 'rgba(34, 197, 94, 0.12)' },
    { label: 'Remaining Principal', value: totals.remainingPrincipalTobePaid, isCurrency: true, icon: <AccountIcon />, color: '#b45309', bg: 'rgba(245, 158, 11, 0.12)' },
    {
      label: 'Net Interest Profit',
      value: totals.profit,
      isCurrency: true,
      icon: <ProfitIcon />,
      color: totals.profit < 0 ? '#dc2626' : '#15803d',
      bg: totals.profit < 0 ? 'rgba(239, 68, 68, 0.12)' : 'rgba(34, 197, 94, 0.12)',
    },
  ];

  const columns = [
    {
      field: 'refNo',
      headerName: 'Ref No',
      minWidth: 110,
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{ bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#0f766e', fontWeight: 700, borderRadius: 1.5 }}
        />
      ),
    },
    {
      field: 'date',
      headerName: 'Date',
      minWidth: 100,
      flex: 0.8,
    },
    {
      field: 'name',
      headerName: 'Customer Name',
      minWidth: 150,
      flex: 1.1,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.875rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'lendedAmount',
      headerName: 'Lended Capital',
      minWidth: 130,
      flex: 1,
      type: 'number',
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '0.875rem' }}>
          ₹{params.value?.toLocaleString() || 0}
        </Typography>
      ),
    },
    {
      field: 'weeklyDue',
      headerName: 'Weekly Due',
      minWidth: 110,
      flex: 0.8,
      type: 'number',
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#936097', fontSize: '0.875rem' }}>
          ₹{params.value?.toLocaleString() || 0}
        </Typography>
      ),
    },
    {
      field: 'totalDues',
      headerName: 'Total Dues',
      minWidth: 100,
      flex: 0.7,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'noOfDuePaid',
      headerName: 'Dues Paid',
      minWidth: 100,
      flex: 0.7,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip label={params.value || 0} size="small" sx={{ bgcolor: 'rgba(34, 197, 94, 0.15)', color: '#16a34a', fontWeight: 700 }} />
      )
    },
    {
      field: 'remainingDues',
      headerName: 'Remaining',
      minWidth: 100,
      flex: 0.7,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'pendingDue',
      headerName: 'Pending',
      minWidth: 100,
      flex: 0.7,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'totalPrincipalDue',
      headerName: 'Principal In Due',
      minWidth: 140,
      flex: 1,
      type: 'number',
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.875rem' }}>
          ₹{params.value?.toLocaleString() || 0}
        </Typography>
      ),
    },
    {
      field: 'totalInterestDue',
      headerName: 'Interest In Due',
      minWidth: 140,
      flex: 1,
      type: 'number',
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 600, color: '#936097', fontSize: '0.875rem' }}>
          ₹{params.value?.toLocaleString() || 0}
        </Typography>
      ),
    },
    {
      field: 'paidDueAmount',
      headerName: 'Amount Paid',
      minWidth: 130,
      flex: 1,
      type: 'number',
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#15803d', fontSize: '0.875rem' }}>
          ₹{params.value?.toLocaleString() || 0}
        </Typography>
      ),
    },
    {
      field: 'remainingPrincipalTobePaid',
      headerName: 'Principal Due Rem.',
      minWidth: 150,
      flex: 1,
      type: 'number',
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 600, color: '#b45309', fontSize: '0.875rem' }}>
          ₹{params.value?.toLocaleString() || 0}
        </Typography>
      ),
    },
    {
      field: 'profit',
      headerName: 'Net Profit',
      minWidth: 120,
      flex: 0.9,
      type: 'number',
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 800,
            color: params.value < 0 ? '#dc2626' : '#15803d',
          }}
        >
          ₹{params.value?.toLocaleString() || 0}
        </Typography>
      ),
    },
    {
      field: 'accountStatus',
      headerName: 'Status',
      minWidth: 130,
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const status = params.value?.toLowerCase() || 'active';
        let bg = 'rgba(119, 200, 199, 0.15)';
        let text = '#0f766e';
        if (status === 'closed/paid' || status === 'closed' || status === 'paid') {
          bg = 'rgba(254, 226, 226, 0.9)';
          text = '#991b1b';
        } else if (status === 'npa') {
          bg = 'rgba(245, 158, 11, 0.15)';
          text = '#b45309';
        }
        return (
          <Chip
            label={status.toUpperCase()}
            size="small"
            sx={{
              bgcolor: bg,
              color: text,
              fontWeight: 700,
              borderRadius: 2,
            }}
          />
        );
      },
    },
  ];

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/velavan_finance/get_all_entries`)
      .then((res) => {
        const formatted = res.data.map((entry, index) => ({
          id: entry._id || index,
          ...entry,
        }));
        setEntries(formatted);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setLoading(false);
      });
  }, []);

  const handleNavigate = (event) => {
    const page = event.target.value;
    setSelectedPage(page);
    if (page) {
      navigate(page);
    }
  };

  return (
    <>
      <ResponsiveAppBar />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 3, md: 5 } }}>
          <Container maxWidth="xl">
            {/* Header Section */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
              <Box>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.6, borderRadius: '50px', bgcolor: 'rgba(119, 200, 199, 0.15)', mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#77c8c7' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#936097', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Financial Intelligence
                  </Typography>
                </Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
                  Interest & Yield Calculations
                </Typography>
                <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                  Detailed mathematical breakdown of principal lending, interest yields & portfolio net profit
                </Typography>
              </Box>

              <FormControl size="small" sx={{ minWidth: 220, bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <InputLabel sx={{ fontWeight: 500, color: '#64748b' }}>Navigate Module</InputLabel>
                <Select
                  value={selectedPage}
                  label="Navigate Module"
                  onChange={handleNavigate}
                  sx={{
                    borderRadius: 3,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(226, 232, 240, 0.9)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#77c8c7' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#936097' },
                  }}
                >
                  <MenuItem value="/admin">Gold & Vehicle Loan</MenuItem>
                  <MenuItem value="/velavan-finance">Velavan Finance Ledger</MenuItem>
                  <MenuItem value="/due-matters">Due Matters</MenuItem>
                  <MenuItem value="/interestCalculation">Interest Calculation</MenuItem>
                  <MenuItem value="/dailyAccounts">Daily Accounts</MenuItem>
                  <MenuItem value="/finalAccounts">Final Accounts</MenuItem>
                  <MenuItem value="/monthly-graph">Monthly Graph Status</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {loading ? (
              <Box sx={{ width: '100%', mt: 2 }}>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[...Array(8)].map((_, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                      <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3.5 }} />
                    </Grid>
                  ))}
                </Grid>
                <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 4 }} />
              </Box>
            ) : (
              <>
                {/* 8 Calculation Metric Cards */}
                <Grid container spacing={2.5} sx={{ mb: 4 }}>
                  {totalCards.map((card, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Card
                        sx={{
                          borderRadius: 3.5,
                          bgcolor: '#ffffff',
                          border: '1px solid rgba(226, 232, 240, 0.9)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.03)',
                          transition: 'all 0.25s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: '0 12px 28px rgba(119, 200, 199, 0.15)',
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 3,
                              bgcolor: card.bg,
                              color: card.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {card.icon}
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748b',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                display: 'block',
                                mb: 0.3,
                              }}
                            >
                              {card.label}
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 800,
                                color: card.color,
                              }}
                            >
                              {card.isCurrency ? `₹${card.value.toLocaleString()}` : card.value.toLocaleString()}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Customer Search Card */}
                <CustomerSearchBar
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  totalCount={entries.length}
                  filteredCount={filteredEntries.length}
                  placeholder="Search by Refer ID (e.g. VF-0001), Customer Name, Contact, or Aadhaar No..."
                  title="Interest Calculations Customer Search"
                  subtitle="Dynamically calculate principal, interest & profits for filtered customers"
                />

                {/* Calculation Ledger DataGrid */}
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    bgcolor: '#ffffff',
                    border: '1px solid rgba(226, 232, 240, 0.9)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)',
                    p: 2.5,
                  }}
                >
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#77c8c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AccountIcon />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        Customer Calculations Ledger
                      </Typography>
                    </Box>
                    <Chip
                      label={`${filteredEntries.length} of ${entries.length} Computed Accounts`}
                      sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 700, borderRadius: 2 }}
                    />
                  </Box>

                  <Box sx={{ height: 560, width: "100%" }}>
                    <DataGrid
                      rows={filteredEntries}
                      columns={columns}
                      loading={loading}
                      getRowId={(row) => row._id || row.id}
                      pageSize={10}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      checkboxSelection
                      disableSelectionOnClick
                      components={{ Toolbar: GridToolbar }}
                      componentsProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 500 },
                        },
                      }}
                      getRowClassName={(params) => {
                        const status = params.row.accountStatus?.toLowerCase();
                        if (status === "closed/paid" || status === "closed" || status === "paid") {
                          return "closed-row";
                        }
                        if (status === "npa") {
                          return "npa-row";
                        }
                        return "active-row";
                      }}
                      sx={{
                        border: 'none',
                        fontSize: { xs: "12px", md: "13.5px" },
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: '#f8fafc',
                          color: '#475569',
                          fontWeight: 700,
                        },
                        '& .closed-row': {
                          backgroundColor: 'rgba(254, 242, 242, 0.85) !important',
                          color: '#991b1b',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: 'rgba(254, 226, 226, 0.95) !important',
                          },
                        },
                        '& .npa-row': {
                          backgroundColor: 'rgba(254, 243, 199, 0.45) !important',
                          color: '#b45309',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: 'rgba(254, 243, 199, 0.7) !important',
                          },
                        },
                        '& .active-row': {
                          backgroundColor: '#ffffff',
                          '&:hover': {
                            backgroundColor: 'rgba(119, 200, 199, 0.08) !important',
                          },
                        },
                        '& .MuiDataGrid-row': {
                          transition: 'background-color 0.15s',
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </>
            )}
          </Container>
        </Box>
      </ThemeProvider>
    </>
  );
};

export default InterestCalculation;